import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, cn, fmtDate, publicAppUrl, whatsappShareUrl } from "../lib/api";
import TopBar from "../components/TopBar";
import { Card, Chip, Empty, Skeleton } from "../components/ui";
import ScoreListCard from "../components/ScoreListCard";
import { ArrowLeft, BarChart3, Share2 } from "lucide-react";
import HmtBanner from "../components/HmtBanner";
import InstagramShareSheet, { InstagramButton } from "../components/InstagramShareSheet";
import { useAuth } from "../context/AuthContext";
import { drawReportCard, reportCaption } from "../lib/reportImage";

export default function PollReportPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { club } = useAuth();
  const [r, setR] = useState(null);
  const [igOpen, setIgOpen] = useState(false);
  useEffect(() => {
    api.get(`/polls/${id}/report`)
      .then(({ data }) => setR(data))
      .catch((err) => { toast.error(apiError(err)); nav(`/polls/${id}`); });
  }, [id, nav]);

  if (!r) {
    return (
      <div className="pb-24 px-5 pt-6 space-y-3">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-24" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const shareText = buildShareText(r, `${publicAppUrl()}/share/day/${id}`);
  const whatsappUrl = whatsappShareUrl(shareText);

  // Open WhatsApp with the full summary pre-filled. The summary is also copied,
  // so it can be pasted if a WhatsApp client drops the pre-filled text.
  const share = async (e) => {
    e.preventDefault();
    const win = window.open(whatsappUrl, "_blank");
    const blocked = !win || win.closed || typeof win.closed === "undefined";
    try {
      await navigator.clipboard?.writeText(shareText);
      toast.success(blocked ? "WhatsApp didn't open — report copied, paste it in your group" : "Report also copied to clipboard");
    } catch {
      if (blocked) toast.error("Couldn't open WhatsApp");
    }
  };

  return (
    <div className="pb-28">
      <HmtBanner />
      <div className="flex items-center justify-between px-3 pt-4">
        <button onClick={() => nav(`/polls/${id}`)} className="h-10 w-10 rounded-full hover:bg-black/5 grid place-items-center" data-testid="back-btn">
          <ArrowLeft size={20} />
        </button>
        {r.matches_total > 0 && (
          <div className="flex items-center gap-2">
          <InstagramButton onClick={() => setIgOpen(true)} data-testid="report-instagram-btn" />
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={share}
            className="h-9 px-3 rounded-xl bg-[#25D366] text-white hover:bg-[#20BD5A] flex items-center gap-1.5 text-sm font-semibold no-underline"
            data-testid="report-share-btn"
          >
            <Share2 size={15} /> WhatsApp
          </a>
          </div>
        )}
      </div>
      <TopBar
        title={r.poll.title || "Match day"}
        subtitle={`${fmtDate(r.poll.date)}${r.poll.ground ? ` • ${r.poll.ground}` : ""}`}
        showClubSwitcher={false}
      />

      <DayReportView r={r} onOpenMatch={(m) => nav(`/scores/${m.id}`)} />
      <InstagramShareSheet
        open={igOpen}
        onClose={() => setIgOpen(false)}
        title="Share match day"
        makeImage={() => drawReportCard(r, { clubName: club?.name })}
        caption={reportCaption(r, club?.name)}
        fileName={`cricso-${r.poll.title || "match-day"}`}
      />
    </div>
  );
}

export function DayReportView({ r, onOpenMatch }) {
  const [tab, setTab] = useState("batting");
  const potd = r.player_of_the_day;
  const bat = r.batter_of_the_day;
  const bowl = r.bowler_of_the_day;
  return r.matches_total === 0 ? (
    <div className="px-5">
      <Card className="p-0"><Empty icon={BarChart3} title="No matches scored yet" subtitle="Start scoring from the poll to build today's report." /></Card>
    </div>
  ) : (
    <div className="px-5 space-y-4">
      <div className="grid grid-cols-4 gap-2" data-testid="report-tiles">
        <Tile label="Matches" value={r.matches_played} hint={r.matches_total > r.matches_played ? `+${r.matches_total - r.matches_played} live` : null} />
        <Tile label="Runs" value={r.total_runs} />
        <Tile label="Wickets" value={r.total_wickets} />
        <Tile label="6s / 4s" value={`${r.total_sixes}/${r.total_fours}`} />
      </div>

      {(potd || bat || bowl) && (
        <Section title="Awards">
          <div className="space-y-3" data-testid="day-awards">
            {potd && (
              <Card className="flex items-center gap-4 bg-amber-50/60 border-amber-200/60" data-testid="player-of-the-day">
                <Trophy big />
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-widest text-amber-700 font-semibold">Player of the day</div>
                  <div className="text-lg font-bold truncate">{potd.name}</div>
                  <div className="text-sm text-ink-muted">{performanceLine(potd)}</div>
                </div>
              </Card>
            )}
            {(bat || bowl) && (
              <div className="grid grid-cols-2 gap-3">
                <Award title="Batsman of the day" p={bat} line={bat && `${bat.runs}${bat.highest_not_out && bat.innings === 1 ? "*" : ""} runs (${bat.balls}) • ${bat.fours}×4 ${bat.sixes}×6`} testid="batter-of-the-day" />
                <Award title="Bowler of the day" p={bowl} line={bowl && `${bowl.wickets}/${bowl.bowl_runs} (${bowl.overs}) • Econ ${bowl.economy.toFixed(1)}`} testid="bowler-of-the-day" />
              </div>
            )}
          </div>
        </Section>
      )}

      {r.standings.length > 0 && (
        <Section title="Standings">
          <Card className="p-0 overflow-hidden">
            <table className="w-full text-sm tabular-nums" data-testid="standings">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-ink-muted">
                  <th className="text-left font-semibold px-4 py-2">Team</th>
                  <th className="font-semibold">P</th>
                  <th className="font-semibold">W</th>
                  <th className="font-semibold">L</th>
                  <th className="font-semibold pr-4">T</th>
                </tr>
              </thead>
              <tbody>
                {r.standings.map((s, i) => (
                  <tr key={s.team} className="border-t border-black/[0.05]">
                    <td className="px-4 py-2.5 font-semibold">
                      {s.team} {i === 0 && s.won > (r.standings[1]?.won ?? -1) && <Chip tone="success" className="ml-1">Leader</Chip>}
                    </td>
                    <td className="text-center">{s.played}</td>
                    <td className="text-center font-bold">{s.won}</td>
                    <td className="text-center">{s.lost}</td>
                    <td className="text-center pr-4">{s.tied}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </Section>
      )}

      <Section title="Results">
        <div className="space-y-3">
          {r.matches.map((m) => <ScoreListCard key={m.id} s={m} onClick={() => onOpenMatch(m)} />)}
        </div>
      </Section>

      <Section title="Player stats">
        <div className="grid grid-cols-3 gap-1 p-1 bg-black/[0.04] rounded-2xl mb-3">
          {[["batting", "Batting"], ["bowling", "Bowling"], ["fielding", "Fielding"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={cn("h-10 rounded-xl text-sm font-semibold", tab === k ? "bg-white shadow-sm text-ink" : "text-ink-muted")}
              data-testid={`report-tab-${k}`}
            >
              {l}
            </button>
          ))}
        </div>
        {tab === "batting" && (
          <StatTable
            rows={r.batting}
            empty="No one has batted yet"
            cols={[
              ["Runs", (p) => <b>{p.runs}</b>],
              ["B", (p) => p.balls],
              ["HS", (p) => `${p.highest}${p.highest_not_out ? "*" : ""}`],
              ["4s/6s", (p) => `${p.fours}/${p.sixes}`],
              ["SR", (p) => p.strike_rate.toFixed(0)],
            ]}
            sub={(p) => `${p.innings} inn${p.average != null ? ` • avg ${p.average}` : ""}`}
          />
        )}
        {tab === "bowling" && (
          <StatTable
            rows={r.bowling}
            empty="No one has bowled yet"
            cols={[
              ["W", (p) => <b>{p.wickets}</b>],
              ["O", (p) => p.overs],
              ["R", (p) => p.bowl_runs],
              ["Best", (p) => p.best_bowling],
              ["Econ", (p) => p.economy.toFixed(1)],
            ]}
            sub={(p) => (p.maidens ? `${p.maidens} maiden${p.maidens === 1 ? "" : "s"}` : null)}
          />
        )}
        {tab === "fielding" && (
          <StatTable
            rows={r.fielding}
            empty="No catches or run outs yet"
            cols={[
              ["Ct", (p) => <b>{p.catches}</b>],
              ["RO", (p) => p.run_outs],
              ["St", (p) => p.stumpings],
            ]}
          />
        )}
      </Section>
    </div>
  );
}

function Tile({ label, value, hint }) {
  return (
    <Card className="p-3 text-center">
      <div className="text-xl font-extrabold tabular-nums leading-tight">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold mt-0.5">{label}</div>
      {hint && <div className="text-[10px] text-ink-muted">{hint}</div>}
    </Card>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-2">{title}</div>
      {children}
    </div>
  );
}

function StatTable({ rows, cols, sub, empty }) {
  if (!rows.length) return <Card className="text-sm text-ink-muted text-center">{empty}</Card>;
  return (
    <Card className="p-0 overflow-hidden">
      <table className="w-full text-sm tabular-nums">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-ink-muted">
            <th className="text-left font-semibold px-4 py-2">Player</th>
            {cols.map(([h]) => <th key={h} className="font-semibold px-1 last:pr-4">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.key} className="border-t border-black/[0.05]">
              <td className="px-4 py-2">
                <div className="font-semibold flex items-center gap-1.5">
                  <span className="truncate max-w-[120px]">{p.name}</span>
                  {p.guest && <span className="text-[10px] uppercase text-amber-700 font-bold">guest</span>}
                </div>
                {sub && sub(p) && <div className="text-[11px] text-ink-muted">{sub(p)}</div>}
              </td>
              {cols.map(([h, f]) => <td key={h} className="text-center px-1 last:pr-4">{f(p)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Trophy({ big }) {
  return (
    <div className={big ? "h-12 w-12 rounded-2xl bg-amber-100 grid place-items-center shrink-0 text-2xl" : "text-2xl leading-none"} aria-hidden>
      🏆
    </div>
  );
}

function Award({ title, p, line, testid }) {
  return (
    <Card className="p-4 min-w-0" data-testid={testid}>
      <Trophy />
      <div className="text-[10px] uppercase tracking-widest text-ink-muted font-semibold mt-2">{title}</div>
      {p ? (
        <>
          <div className="font-bold truncate">{p.name}</div>
          <div className="text-xs text-ink-muted mt-0.5">{line}</div>
        </>
      ) : (
        <div className="text-sm text-ink-muted mt-0.5">Not yet</div>
      )}
    </Card>
  );
}

function performanceLine(p) {
  const parts = [];
  if (p.innings) parts.push(`${p.runs} runs (${p.balls})`);
  if (p.wickets) parts.push(`${p.wickets} wkt${p.wickets === 1 ? "" : "s"}`);
  const field = p.catches + p.run_outs + p.stumpings;
  if (field) parts.push(`${field} dismissal${field === 1 ? "" : "s"} in the field`);
  return parts.join(" • ");
}

export function buildShareText(r, link) {
  const lines = [`🏏 *${r.poll.title || "Match day"}* — ${fmtDate(r.poll.date)}`, `${r.matches_played} matches played`, ""];
  r.matches.forEach((m, i) => {
    const score = (t) => {
      const s = m.scores.find((x) => x.team === t);
      return s ? `${s.runs}/${s.wickets} (${s.overs})` : "-";
    };
    lines.push(`${i + 1}. ${m.teams[0]} ${score(0)} v ${m.teams[1]} ${score(1)}`);
    if (m.result) lines.push(`   ${m.result}`);
  });
  if (r.standings.length) {
    lines.push("", "*Standings*");
    r.standings.forEach((s) => lines.push(`${s.team}: ${s.won}W ${s.lost}L${s.tied ? ` ${s.tied}T` : ""}`));
  }
  if (r.batting.length) {
    lines.push("", "*Top batters*");
    r.batting.slice(0, 3).forEach((p) => lines.push(`${p.name} — ${p.runs} (${p.balls})`));
  }
  if (r.bowling.length) {
    lines.push("", "*Top bowlers*");
    r.bowling.slice(0, 3).forEach((p) => lines.push(`${p.name} — ${p.wickets}/${p.bowl_runs} (${p.overs})`));
  }
  const awards = [
    r.player_of_the_day && `🏆 Player of the day: ${r.player_of_the_day.name}`,
    r.batter_of_the_day && `🏆 Batsman of the day: ${r.batter_of_the_day.name} — ${r.batter_of_the_day.runs} (${r.batter_of_the_day.balls})`,
    r.bowler_of_the_day && `🏆 Bowler of the day: ${r.bowler_of_the_day.name} — ${r.bowler_of_the_day.wickets}/${r.bowler_of_the_day.bowl_runs} (${r.bowler_of_the_day.overs})`,
  ].filter(Boolean);
  if (awards.length) lines.push("", ...awards);
  if (link) lines.push("", `📊 Full scorecards: ${link}`);
  lines.push("", "Thanks,", "Cricso", "Powered by HMT Technologies");
  return lines.join("\n");
}
